import { useParams, useNavigate } from 'react-router-dom'
import { useExperiment, useExperimentTimeline, useApproveExperiment, useRejectExperiment, useRollbackExperiment, persistenzBefundAusApprove, type ManualAction, type PersistenceWarningKind, type PersistenzBefund } from '@/api/experiments'
import { useState, useEffect, useRef } from 'react'

const OUTCOME_BADGE: Record<string, { label: string; cls: string }> = {
  success: { label: 'Erfolg', cls: 'bg-green-100 text-green-800' },
  neutral: { label: 'Neutral', cls: 'bg-gray-100 text-gray-700' },
  failure: { label: 'Fehlschlag', cls: 'bg-red-100 text-red-800' },
}

export default function ExperimentDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const expId = parseInt(id ?? '', 10)

  // `refetch()` liefert das Ergebnis GENAU DIESES Nachladens zurueck — der Beleg, den
  // Uebergang 5 unten braucht. Ein globaler Zeitstempel taete es nicht (siehe dort).
  const { data, isLoading, refetch } = useExperiment(expId)
  const { data: timelineData } = useExperimentTimeline(expId)
  const approveMut = useApproveExperiment()
  const rejectMut = useRejectExperiment()
  const rollbackMut = useRollbackExperiment()
  const [rollbackReason, setRollbackReason] = useState('')

  // ════ M20-UI (Scheibe F) — LEBENSZYKLUS DES VERDIKTS ════════════════════════════════
  //
  //   INVARIANTE: Sichtbar ist ein Verdikt genau dann, wenn es zur AKTUELLEN
  //   Routen-Sitzung gehoert, zum AKTUELL gezeigten Experiment, und der geladene
  //   Serverzustand es nicht ueberholt hat.
  //
  // Die fuenf Uebergaenge folgen daraus und stehen unten je an ihrer Stelle. Nichts davon
  // ueberlebt einen Reload — das ist die Scope-Grenze dieses Moduls; ein durables,
  // serverseitig lesbares Verdikt ist D-A5 und offen.
  //
  // WARUM ALS EINE REGEL: Die Durchgaenge 5, 6 und 7 fanden dreimal hintereinander die
  // Gegenrichtung des jeweils vorigen Einzelfixes. Belege und Herleitung:
  // `.superpowers/review/scheibe-f/durchgang-{5,6,7}/codex-review-VERDIKT.md`.
  const [verdikt, setVerdikt] = useState<{ expId: number; art: ManualAction; diagnose?: string } | null>(null)

  // Derselbe Lebenszyklus, zweiter Inhalt: Ein Rollback antwortet mit HTTP 200 auch dann,
  // wenn der Amazon-Revert zwar committet ist, die lokale Verbuchung oder der Status-Reload
  // aber gescheitert sind (`persistenceWarning`, `experiments/routes.ts` ~356/363).
  // Das ist KEIN Fehlerfall — es loest weder `ApiError` noch den globalen Toast aus.
  // Ohne diesen Hinweis loeschte der Rollback das M20-Verdikt und der Operator saehe
  // anschliessend ueberhaupt keine Warnung mehr (Codex-Durchgang 8, Finding 1).
  //
  // Codex-Durchgang 9, Finding 1: Der Hinweis hing an KEINER ID — beim Parameterwechsel
  // konnte er im ersten Bild des neuen Experiments erscheinen, bevor der Effekt greift.
  // Deshalb dieselbe Form wie beim Verdikt: `{ expId, text }`, gerendert nur fuer das
  // aktuell gezeigte Experiment.
  const [persistenzHinweis, setPersistenzHinweis] = useState<
    { expId: number; text: string; art?: PersistenceWarningKind } | null
  >(null)

  // Uebergang 1 — Parameterwechsel BEENDET die Sitzung, fuer BEIDE Inhalte. `useParams`
  // wechselt ohne Remount; ohne diesen Effekt waeren die ID-Pruefungen beim Rendern nur
  // Filter, die verbergen statt zu beenden — bei Rueckkehr auf dieselbe ID kaeme alles
  // wieder.
  const sitzung = useRef(0)
  useEffect(() => {
    sitzung.current += 1
    setVerdikt(null)
    setPersistenzHinweis(null)
  }, [expId])

  /**
   * Zeigt einen Persistenzbefund an — und beendet ihn nach derselben Regel, egal aus
   * welchem Schreibweg er stammt (Scheibe M).
   *
   * EIN LEBENSZYKLUS, NICHT ZWEI: Genehmigung und Rollback koennen beide eine
   * Persistenzwarnung liefern. Zwei Zustaende mit je eigener Endebedingung waeren genau
   * die Konstruktion, die in Scheibe F die Durchgaenge 5-7 gekostet hat — dreimal fand
   * der Reviewer die Gegenrichtung des jeweils vorigen Einzelfixes. Die Invariante im
   * Kopf dieser Datei gilt unveraendert; nur die Zahl der Quellen ist gewachsen.
   */
  async function befundZeigen(id: number, meine: number, befund: PersistenzBefund | null) {
    // OHNE BEFUND WIRD NICHT EINFACH ABGEBROCHEN (Gemini-Zweitreview, Finding 2):
    // Ein Schreibvorgang, der KEINE Warnung meldet, kann eine noch stehende
    // 'status-reload'-Warnung eines FRUEHEREN Vorgangs erledigen — sie besagt nur „die
    // Anzeige koennte veraltet sein", und genau das klaert ein gelungenes Nachladen.
    // Vorher blieb sie stehen, obwohl die Seite laengst frische Daten hatte; die
    // Oberflaeche behauptete damit etwas Unwahres. Erreichbar wurde diese Lage erst
    // durch dieses Modul: Vor Scheibe M konnte ein Hinweis nur aus dem Rollback kommen,
    // und zweimal zurueckrollen geht nicht.
    //
    // Eine 'local-persistence'-Warnung raeumt das NICHT weg — die Verbuchungsluecke
    // eines frueheren Vorgangs repariert kein spaeterer Write.
    if (befund) {
    // FAIL-CLOSED ZWISCHEN SCHREIBVORGAENGEN: Ein bestehender 'local-persistence'-Befund
    // wird NIE von einem spaeteren 'status-reload' verdraengt.
    //
    // WARUM (Codex-Endstand, Finding 2): Erreichbare Folge — Approve meldet eine
    // Verbuchungsluecke, das Experiment steht danach auf `running`, der Operator rollt
    // zurueck, und dessen Status-Reload scheitert. Ohne diese Regel ersetzte die
    // schwaechere Meldung die staerkere, und der anschliessend gelungene Refetch
    // loeschte sie ganz — die Audit-Luecke des Vorwaerts-Writes waere spurlos weg,
    // obwohl ein Rollback sie nicht repariert.
    //
    // Das ist dieselbe Regel wie beim Diskriminator selbst, nur ueber Vorgaenge hinweg
    // statt innerhalb einer Antwort: Es gewinnt die Ursache, die sich nicht von selbst
    // aufloest.
    // Kein Merker fuer „wurde verdraengt": Der Setter am Ende von Uebergang 5 raeumt
    // ohnehin nur auf, wenn dort dann ein 'status-reload' steht. Bleibt der staerkere
    // Befund stehen, laeuft der Refetch ins Leere — das kostet einen Aufruf und ist
    // korrekt. Ein aus einem State-Updater herausgereichtes Flag waere dagegen nicht
    // verlaesslich, weil der Updater nicht garantiert synchron laeuft.
      setPersistenzHinweis(vorher =>
        vorher?.art === 'local-persistence' && befund.art === 'status-reload'
          ? vorher
          : { expId: id, text: befund.text, art: befund.art })

      // Eine 'local-persistence'-Warnung loest kein Refetch auf — hier ist Schluss.
      if (befund.art !== 'status-reload') return
    }

    // Uebergang 5 — eine reine STATUS-RELOAD-Warnung endet, wenn GENAU DIESES Nachladen
    // gelingt. Sie besagt, dass der Status-Reload fehlschlug; ein gelungener Reload
    // erledigt damit ihren gesamten Inhalt. Eine 'local-persistence'-Warnung bleibt — die
    // loest kein Refetch auf, deshalb wird hier gar nicht erst nachgeladen.
    //
    // WARUM DAS ERGEBNIS DES AUFRUFS UND KEIN ZEITSTEMPEL: Ein Vergleich von
    // `dataUpdatedAt` gegen den Stand vor dem Absenden belegt nur „irgendein Fetch war
    // seit dem Klick erfolgreich" — den erfuellt auch ein Focus-Refetch, der waehrend des
    // Schreibvorgangs noch den ALTEN Zustand geladen hat (`refetchOnWindowFocus` ist an,
    // `main.tsx` setzt es nicht ab). Der Hinweis verschwaende dann, obwohl das Nachladen
    // DANACH scheiterte. Beleg und Szenario: Scheibe F, Durchgang 10, Finding 1.
    //
    // NUR DIESER EINE VERSUCH — und das steht so auch in der Beschriftung. Scheitert er,
    // bleibt der Hinweis fuer diese Routen-Sitzung stehen. Ihn an einen spaeteren Fetch
    // zu binden hiesse, genau die Messlatte wieder einzufuehren, die oben erledigt ist.
    //
    // DIE URSACHE WIRD ZWEIMAL GEPRUEFT — hier und unten im Setter. Das ist Absicht:
    // hier, um gar nicht erst unnoetig nachzuladen; unten, weil zwischen `await` und
    // Setzen ein NEUER Hinweis anderer Ursache stehen kann, den dieser Aufruf nicht
    // beenden darf. GEMESSEN (Scheibe F, Proben M34b/M34d/M34e): Entfernt man EINE der
    // beiden, bleibt alles gruen — sie decken einander. Entfernt man BEIDE, werden 3 rot.
    // Die Deckung liegt auf dem PAAR, nicht auf je einer Zeile; das steht hier, damit
    // niemand eine davon fuer tot haelt und streicht.
    // Hierher fuehren ZWEI Wege: ein neuer 'status-reload'-Befund, oder gar kein Befund
    // bei noch stehender alter Warnung. Fuer beide gilt dieselbe Beweislast.
    //
    // KOSTEN, korrekt benannt: Bei einem Schreibvorgang ohne Warnung laeuft dieses
    // Nachladen auch dann, wenn gar kein Hinweis steht — und es ist ein ZUSAETZLICHER
    // GET. Hier stand vorher, es sei „derselbe" Aufruf, weil `refetch()` den von der
    // Invalidierung angestossenen Lauf mit `cancelRefetch` ersetzt. Das stimmt auf
    // TanStack-Ebene, aber NICHT auf Netz-Ebene: `useExperiment` reicht kein
    // `AbortSignal` an `api.get` weiter (`api/experiments.ts` ~264), also laeuft der
    // erste Browser-Fetch weiter und ein zweiter startet. Gemessen sichtbar in den
    // Tests, die genau zwei Reads scheitern lassen muessen.
    //
    // Warnungsverlust entsteht daraus nicht: Der abgeloeste Aufrufer laeuft auf dem
    // Ersatzlauf mit; scheitert der, melden beide `isSuccess === false`. Es bleibt
    // Mehraufwand. Ein Merker im Ref waere doppelter Zustand, der gegenueber dem echten
    // auseinanderlaufen kann — die Abkuerzung waere den Preis nicht wert.
    // (Richtigstellung nach Codex-Re-Review 3, Finding 2.)
    const nachgeladen = await refetch()
    if (meine !== sitzung.current || !nachgeladen.isSuccess) return
    setPersistenzHinweis(vorher => (vorher?.expId === id && vorher.art === 'status-reload' ? null : vorher))
  }

  function genehmigen(id: number) {
    const meine = sitzung.current
    approveMut
      .mutateAsync(id)
      // Uebergang 2 — eine Antwort, deren Sitzung beendet ist, wird VERWORFEN. Sonst
      // schriebe eine verspaetete Antwort ein Verdikt in eine Sitzung, die es nie
      // angefordert hat.
      .then(async antwort => {
        if (meine !== sitzung.current) return
        if (antwort.manualAction) setVerdikt({ expId: id, art: antwort.manualAction, diagnose: antwort.execution?.error })
        // Verdikt und Persistenzwarnung schliessen einander NICHT aus: Der Executor kann
        // einen ungeklaerten Amazon-Ausgang UND eine unvollstaendige Verbuchung melden.
        await befundZeigen(id, meine, persistenzBefundAusApprove(antwort))
      })
      // Fehler meldet der globale MutationCache-Toast (main.tsx). Hier nur abfangen,
      // damit aus dem `mutateAsync` keine unbehandelte Rejection wird.
      .catch(() => {})
  }

  function zuruecksetzen(id: number, grund: string) {
    const meine = sitzung.current
    rollbackMut
      .mutateAsync({ id, reason: grund })
      // Uebergang 3 — ein ERFOLGREICHER Rollback beendet das Verdikt, aber nur in seiner
      // eigenen Sitzung. Ohne die Sitzungspruefung loeschte ein verspaetet erfolgreicher
      // Rollback das inzwischen gesetzte Verdikt eines ANDEREN Experiments — die einzige
      // Richtung dieses Moduls, die eine Warnung UNTERDRUECKT statt sie zu doppeln.
      // Ein FEHLGESCHLAGENER Rollback beendet nichts: Dann ist der Befund weiterhin die
      // einzige Information ueber den ungeklaerten Ausgang.
      .then(async antwort => {
        if (meine !== sitzung.current) return
        // Das M20-Verdikt DARF hier verschwinden: Der Amazon-Ausgang ist geklaert.
        // Aber ein 200 mit `persistenceWarning` heisst nicht „alles in Ordnung" — dann
        // ist der lokale Stand unsicher, und ohne den Hinweis unten bliebe davon nichts
        // uebrig ausser einer Anzeige, die vielleicht veraltet ist.
        setVerdikt(null)
        // Derselbe Weg wie beim Approve — Uebergang 5 steht genau einmal, in
        // `befundZeigen` (Scheibe M). `warningKind` liefert hier das Backend selbst;
        // auf den Approve-Wegen gibt es das Feld nicht und die Ursache wird aus der
        // Struktur abgeleitet. Beide muenden in denselben Lebenszyklus.
        await befundZeigen(id, meine, antwort.persistenceWarning
          ? { text: antwort.persistenceWarning, art: antwort.warningKind ?? 'local-persistence' }
          : null)
      })
      .catch(() => {})
  }

  if (isLoading) return <div className="py-12 text-center text-muted-foreground">Laden...</div>
  if (!data?.experiment) return <div className="py-12 text-center text-muted-foreground">Experiment nicht gefunden</div>

  const { experiment: exp, baseline, evaluation } = data

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button onClick={() => navigate('/experiments')} className="text-sm text-muted-foreground hover:underline mb-1 block">&larr; Zurück</button>
          <h1 className="text-2xl font-bold">Experiment #{exp.id}</h1>
          <div className="mt-1 flex gap-2 text-sm">
            <span className="rounded bg-gray-100 px-2 py-0.5 font-mono">{exp.experimentType}</span>
            <span className={`rounded px-2 py-0.5 ${exp.riskLevel === 'high' ? 'bg-red-100 text-red-800' : exp.riskLevel === 'medium' ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'}`}>{exp.riskLevel}</span>
            <span className="rounded bg-blue-100 px-2 py-0.5 text-blue-800">{exp.source}</span>
          </div>
        </div>
        <div className="flex gap-2">
          {exp.status === 'proposed' && (
            <>
              <button onClick={() => genehmigen(exp.id)} disabled={approveMut.isPending} className="rounded-md bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50">Genehmigen</button>
              <button onClick={() => rejectMut.mutate({ id: exp.id })} className="rounded-md bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700">Ablehnen</button>
            </>
          )}
          {(exp.status === 'running' || exp.status === 'evaluated') && (
            <div className="flex gap-2 items-center">
              <input value={rollbackReason} onChange={e => setRollbackReason(e.target.value)} placeholder="Rollback-Grund..." className="rounded border px-3 py-2 text-sm" />
              <button onClick={() => zuruecksetzen(exp.id, rollbackReason)} disabled={rollbackReason.length < 3} className="rounded-md bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700 disabled:opacity-50">Rollback</button>
            </div>
          )}
        </div>
      </div>

      {/* M20-UI (Scheibe F): Der Ausgang, den ein Mensch anfassen muss.
          Der Text BESCHREIBT den Zustand und verspricht KEINEN Aufloesungsweg —
          der Betriebsvorgang dafuer ist Registerpunkt D-A5 und offen. */}
      {/* Rollback bestaetigt, lokaler Stand unsicher. Eigener Block statt Verlaengerung des
          M20-Verdikts: Die Aussage ist eine ANDERE — bei M20 ist der Amazon-Ausgang
          unbekannt, hier ist er bestaetigt und die lokale Verbuchung unvollstaendig. */}
      {persistenzHinweis?.expId === exp.id && (
        <div data-testid="persistenz-hinweis" className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          {/* Zwei Ursachen, zwei Aussagen — seit `warningKind` unterscheidbar, ohne den
              Backend-Freitext zu parsen (Koordinator-Entscheidung 2026-08-05).
              Der Freitext bleibt darunter als Diagnose-Ebene. */}
          <div className="font-semibold">
            {persistenzHinweis.art === 'status-reload'
              ? 'Bei Amazon bestätigt — Status konnte nicht nachgeladen werden'
              : 'Bei Amazon bestätigt — lokale Nachverarbeitung war unvollständig'}
          </div>
          <p className="mt-1">
            {persistenzHinweis.art === 'status-reload'
              ? 'Der Schreibvorgang bei Amazon ist erfolgt und lokal verbucht. Nur das unmittelbare Nachladen des Status ist fehlgeschlagen, deshalb kann die Anzeige veraltet sein. Der Hinweis verschwindet, sobald der unmittelbar folgende Nachladeversuch gelingt. Scheitert auch der, bleibt er stehen, bis die Seite neu geladen wird — auch wenn die Anzeige zwischenzeitlich wieder aktuell ist.'
              : 'Der Schreibvorgang bei Amazon wurde bestätigt. Die lokale Nachverarbeitung dieses Vorgangs war unvollständig — Verbuchung oder Audit-Eintrag. Prüfe den Status dieses Experiments; ein Neuladen der Seite behebt das nicht.'}
          </p>
          {/* Freier Backend-Text: eingeklappt und als Nicht-Handlungsanweisung beschriftet —
              dieselbe Regel wie beim M20-Verdikt (Codex-Durchgang 4, Finding 2). */}
          <details data-testid="persistenz-diagnose" className="mt-2 border-t pt-2">
            <summary className="cursor-pointer text-xs opacity-80">
              Diagnose – keine Handlungsanweisung
            </summary>
            <p className="mt-1 font-mono text-xs opacity-70">{persistenzHinweis.text}</p>
          </details>
        </div>
      )}

      {/* Uebergang 4 — der geladene Serverzustand ueberholt das Verdikt. Bewusst ABGELEITET
          statt in einem Effekt geloescht: Eine Ableitung kann nicht veralten, ein Loeschen
          konkurriert mit dem naechsten Refetch. Nur `rolled_back` zaehlt — es beweist, dass
          der Amazon-Zustand zurueckgesetzt wurde. `cancelled` beweist das NICHT (ein bereits
          erfolgter Write bleibt bestehen) und blendet deshalb nicht aus.
          Die ID-Pruefung steht neben Uebergang 1, nicht statt seiner: Sie deckt das eine
          Bild zwischen Parameterwechsel und Effektlauf ab.

          ⚠️ DIESE ID-PRUEFUNG IST NICHT TEST-GEBUNDEN — gemessen, nicht vermutet: Entfernt
          man sie (Probe M25b), bleiben alle 15 Tests gruen. Grund: `fireEvent` flusht die
          Effekte, bevor eine Zusicherung hinsehen kann; das Zwischenbild ist in jsdom
          nicht beobachtbar. Im Browser ist es das — ein Frame mit dem Verdikt des alten
          Experiments ueber der Ueberschrift des neuen. Sie bleibt deshalb stehen, aber
          niemand soll glauben, ein Test halte sie fest. Dieselbe Messung gilt fuer die
          ID-Pruefung am Persistenz-Hinweis (Probe M31a). */}
      {verdikt?.expId === exp.id && exp.status !== 'rolled_back' && (
        <div
          data-testid="m20-verdict"
          className={`rounded-md border p-4 text-sm ${
            verdikt.art === 'retry-safe'
              ? 'border-orange-300 bg-orange-50 text-orange-900'
              : 'border-red-300 bg-red-50 text-red-900'
          }`}
        >
          <div className="font-semibold">
            {verdikt.art === 'retry-safe'
              ? 'Genehmigt, aber nicht ausgeführt — manuelle Prüfung nötig'
              : 'Genehmigt, Amazon-Ausgang unbestätigt — NICHT erneut ausführen'}
          </div>
          <p className="mt-1">
            {verdikt.art === 'retry-safe'
              ? 'Der Amazon-Istwert weicht vom erwarteten Vorzustand ab. Es wurde nachweislich nichts geschrieben. Das Experiment steht jetzt auf „laufend" und wird von hier aus nicht erneut ausgeführt. Prüfe den Zustand bei Amazon.'
              : 'Ein Amazon-Write kann bereits erfolgt sein. Ein erneuter Versuch könnte ihn doppeln. Prüfe den Ist-Zustand bei Amazon, bevor du weiter handelst.'}
          </p>
          {/* Technische Diagnose — EINGEKLAPPT und ausdrücklich als Nicht-Handlungsanweisung
              beschriftet.
              Dieser Text kommt unveraendert aus dem Backend und ist damit der einzige
              Textknoten des Blocks, den die Tests NICHT binden koennen — sie sehen nur
              ihren eigenen Mock. Meine fruehere Behauptung „jeder sichtbare Textknoten ist
              gebunden" war deshalb falsch (Codex-Durchgang 3, Finding 3).
              Erste Korrektur setzte ihn nur ab und beschriftete ihn mit „Technische
              Meldung:". Das reichte nicht (Codex-Durchgang 4, Finding 2): Die Beschriftung
              sagte nicht, dass der folgende Inhalt KEINE Anweisung ist, und der Text stand
              unmittelbar nach der kontrollierten Aussage — auch fuer Screenreader.
              Jetzt zugeklappt: Der Operator liest ihn nur, wenn er ihn ausdruecklich
              aufklappt, und die Zusammenfassung sagt ihm vorher, was er dort NICHT findet.
              Der diagnostische Wert bleibt (welcher Istwert wich ab), die handlungsleitende
              Flaeche bleibt sauber. (User-Entscheidung 2026-08-04.) */}
          {verdikt.diagnose && (
            <details data-testid="m20-diagnose" className="mt-2 border-t pt-2">
              <summary className="cursor-pointer text-xs opacity-80">
                Diagnose – keine Handlungsanweisung
              </summary>
              <p className="mt-1 font-mono text-xs opacity-70">{verdikt.diagnose}</p>
            </details>
          )}
        </div>
      )}

      {/* Hypothese */}
      <div className="rounded-lg border p-4">
        <h3 className="text-sm font-semibold text-muted-foreground">Hypothese</h3>
        <p className="mt-1">{exp.hypothesis}</p>
        <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Erwartetes Ergebnis:</span>
            <pre className="mt-1 rounded bg-muted p-2 text-xs overflow-auto">{JSON.stringify(exp.expectedOutcome, null, 2)}</pre>
          </div>
          <div>
            <span className="text-muted-foreground">Änderungs-Details:</span>
            <pre className="mt-1 rounded bg-muted p-2 text-xs overflow-auto">{JSON.stringify(exp.changeDetails, null, 2)}</pre>
          </div>
        </div>
      </div>

      {/* Baseline vs. Ergebnis */}
      {baseline && evaluation && (
        <div className="rounded-lg border p-4">
          <h3 className="text-sm font-semibold text-muted-foreground">Baseline vs. Ergebnis</h3>
          <div className="mt-3 flex items-center gap-4 mb-4">
            <span className={`rounded px-3 py-1 text-sm font-medium ${OUTCOME_BADGE[evaluation.outcome]?.cls ?? ''}`}>
              {OUTCOME_BADGE[evaluation.outcome]?.label ?? evaluation.outcome}
            </span>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Confidence:</span>
              <div className="h-2 w-24 rounded-full bg-muted overflow-hidden">
                <div className="h-full rounded-full bg-blue-500" style={{ width: `${evaluation.confidenceScore}%` }} />
              </div>
              <span className="text-xs font-medium">{evaluation.confidenceScore}%</span>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-3 py-2 text-left">Metrik</th>
                <th className="px-3 py-2 text-right">Baseline</th>
                <th className="px-3 py-2 text-right">Ergebnis</th>
                <th className="px-3 py-2 text-right">Delta</th>
              </tr>
            </thead>
            <tbody>
              <MetricRow label="Impressions" base={baseline.impressions} result={evaluation.impressions} delta={evaluation.deltaImpressionsPercent} suffix="%" />
              <MetricRow label="Clicks" base={baseline.clicks} result={evaluation.clicks} delta={evaluation.deltaClicksPercent} suffix="%" />
              <MetricRow label="Spend" base={baseline.spend} result={evaluation.spend} delta={evaluation.deltaSpendPercent} suffix="%" prefix="€" />
              <MetricRow label="Sales" base={baseline.sales} result={evaluation.sales} delta={evaluation.deltaSalesPercent} suffix="%" prefix="€" />
              <MetricRow label="ACOS" base={baseline.acos} result={evaluation.acos} delta={evaluation.deltaAcosAbsolute} suffix="pp" />
              <MetricRow label="ROAS" base={baseline.roas} result={evaluation.roas} delta={evaluation.deltaRoasPercent} suffix="%" />
            </tbody>
          </table>
          {evaluation.aiSummary && (
            <div className="mt-4 rounded bg-blue-50 p-3 text-sm">
              <span className="font-semibold">AI-Zusammenfassung:</span> {evaluation.aiSummary}
            </div>
          )}
        </div>
      )}

      {/* Timeline */}
      {timelineData?.timeline && timelineData.timeline.length > 0 && (
        <div className="rounded-lg border p-4">
          <h3 className="text-sm font-semibold text-muted-foreground mb-3">Timeline</h3>
          <div className="space-y-2">
            {timelineData.timeline.map((evt, i) => (
              <div key={i} className="flex items-start gap-3 text-sm">
                <span className="mt-0.5 h-2 w-2 rounded-full bg-blue-500 shrink-0" />
                <div>
                  <span className="font-medium">{evt.event}</span>
                  {evt.timestamp && (
                    <span className="ml-2 text-xs text-muted-foreground">
                      {new Date(evt.timestamp).toLocaleString('de-DE')}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function MetricRow({ label, base, result, delta, suffix, prefix }: {
  label: string; base: string | number | null; result: string | number | null; delta: string | null; suffix?: string; prefix?: string
}) {
  const deltaNum = delta ? parseFloat(delta) : null
  return (
    <tr className="border-t">
      <td className="px-3 py-1.5 font-medium">{label}</td>
      <td className="px-3 py-1.5 text-right">{prefix}{base ?? '-'}</td>
      <td className="px-3 py-1.5 text-right">{prefix}{result ?? '-'}</td>
      <td className={`px-3 py-1.5 text-right ${deltaNum !== null ? (deltaNum > 0 ? 'text-green-600' : deltaNum < 0 ? 'text-red-600' : '') : ''}`}>
        {deltaNum !== null ? `${deltaNum > 0 ? '+' : ''}${delta}${suffix ?? ''}` : '-'}
      </td>
    </tr>
  )
}
